/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";

type NavigationView = "home" | "lookbook" | "stories" | "inquiry" | "about" | "how-it-works";

interface NavigationProps {
  onNavClick: (view: NavigationView) => void;
  currentView: string;
}

export default function Navigation({ onNavClick, currentView }: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-[#F5F2ED]/95 backdrop-blur-md border-b border-[#1A1A1A] text-[#1A1A1A]">
      {/* Editorial Announcement Banner */}
      <div className="bg-[#1A1A1A] text-[#F5F2ED] text-[10px] tracking-[0.25em] py-2 text-center uppercase font-display select-none">
        Join the Waitlist • 2026/2027 Destination Bookings Open
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
        {/* Left Side: Navigation Links */}
        <nav className="hidden lg:flex space-x-6 text-[10px] xl:text-[11px] uppercase tracking-[0.2em] font-sans font-light">
          <button
            id="nav-lookbook"
            onClick={() => onNavClick("lookbook")}
            className={`cursor-pointer transition duration-300 hover:text-black ${
              currentView === "lookbook" ? "font-semibold text-black underline underline-offset-4" : "text-[#7f7d78]"
            }`}
          >
            Lookbook
          </button>
          <button
            id="nav-stories"
            onClick={() => onNavClick("stories")}
            className={`cursor-pointer transition duration-300 hover:text-black ${
              currentView === "stories" ? "font-semibold text-black underline underline-offset-4" : "text-[#7f7d78]"
            }`}
          >
            Stories
          </button>
          <button
            id="nav-about"
            onClick={() => onNavClick("about")}
            className={`cursor-pointer transition duration-300 hover:text-black ${
              currentView === "about" ? "font-semibold text-black underline underline-offset-4" : "text-[#7f7d78]"
            }`}
          >
            About
          </button>
          <button
            id="nav-how-it-works"
            onClick={() => onNavClick("how-it-works")}
            className={`cursor-pointer transition duration-300 hover:text-black ${
              currentView === "how-it-works" ? "font-semibold text-black underline underline-offset-4" : "text-[#7f7d78]"
            }`}
          >
            How it Works
          </button>
        </nav>

        {/* Center: Brand Identity */}
        <div className="flex flex-col items-center justify-center text-center select-none">
          <button
            id="logo-brand"
            onClick={() => onNavClick("home")}
            className="group cursor-pointer flex flex-col items-center"
          >
            <span className="font-serif text-[21px] sm:text-[25px] font-light tracking-[0.15em] uppercase transition-all duration-500 group-hover:tracking-[0.2em]">
              THE ROSE PHOTO
            </span>
            <span className="text-[8px] tracking-[0.45em] uppercase text-[#8c8a81] -mt-0.5 pl-1">
              vogue • editorial • fine art
            </span>
          </button>
        </div>

        {/* Right Side Actions */}
        <div className="hidden lg:flex items-center space-x-6">
          {/* Bespoke Inquiry CTA */}
          <button
            id="nav-cta"
            onClick={() => onNavClick("inquiry")}
            className="bg-[#1A1A1A] cursor-pointer text-[#F5F2ED] text-[10px] xl:text-[10.5px] uppercase tracking-[0.2em] font-sans px-5 py-3 hover:bg-neutral-800 transition duration-500 flex items-center space-x-2 border border-[#1A1A1A] min-h-[44px]"
          >
            <span>JOIN THE WAITLIST</span>
            <ArrowRight size={12} />
          </button>
        </div>

        {/* Mobile menu trigger */}
        <div className="lg:hidden flex items-center space-x-4">
          <button
            id="btn-hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-black p-2 hover:opacity-75 transition min-w-[44px] flex items-center justify-center"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-[#1A1A1A] bg-[#F5F2ED] text-[#1A1A1A] px-6 py-6 space-y-4 flex flex-col justify-start absolute left-0 w-full shadow-lg transition-all duration-300">
          <button
            id="m-nav-home"
            onClick={() => {
              onNavClick("home");
              setMobileMenuOpen(false);
            }}
            className="text-left py-2.5 text-xs uppercase tracking-[0.25em] font-light border-b border-[#1A1A1A]/10 hover:font-bold min-h-[44px]"
          >
            Home / Entrance
          </button>
          <button
            id="m-nav-lookbook"
            onClick={() => {
              onNavClick("lookbook");
              setMobileMenuOpen(false);
            }}
            className="text-left py-2.5 text-xs uppercase tracking-[0.25em] font-light border-b border-[#1A1A1A]/10 hover:font-bold min-h-[44px]"
          >
            The Lookbook
          </button>
          <button
            id="m-nav-stories"
            onClick={() => {
              onNavClick("stories");
              setMobileMenuOpen(false);
            }}
            className="text-left py-2.5 text-xs uppercase tracking-[0.25em] font-light border-b border-[#1A1A1A]/10 hover:font-bold min-h-[44px]"
          >
            The Stories
          </button>
          <button
            id="m-nav-about"
            onClick={() => {
              onNavClick("about");
              setMobileMenuOpen(false);
            }}
            className="text-left py-2.5 text-xs uppercase tracking-[0.25em] font-light border-b border-[#1A1A1A]/10 hover:font-bold min-h-[44px]"
          >
            About Ethos
          </button>
          <button
            id="m-nav-how-it-works"
            onClick={() => {
              onNavClick("how-it-works");
              setMobileMenuOpen(false);
            }}
            className="text-left py-2.5 text-xs uppercase tracking-[0.25em] font-light border-b border-[#1A1A1A]/10 hover:font-bold min-h-[44px]"
          >
            How It Works
          </button>
          <button
            id="m-nav-inquiry"
            onClick={() => {
              onNavClick("inquiry");
              setMobileMenuOpen(false);
            }}
            className="w-full bg-[#1A1A1A] text-[#F5F2ED] py-4 text-center text-[11px] uppercase tracking-[0.2em] font-sans hover:bg-neutral-800 transition min-h-[44px] flex items-center justify-center font-semibold"
          >
            JOIN THE WAITLIST
          </button>
        </div>
      )}
    </header>
  );
}
